import { useState, useEffect } from 'react';
import axios from 'axios';
import { TrendingUp, TrendingDown, Info, Building2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useCompanySettings } from '@/context/CompanySettingsContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Benchmarking = () => {
  const [benchmarks, setBenchmarks] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(true);
  const { industry } = useCompanySettings();

  useEffect(() => {
    fetchIndustries();
  }, []);

  useEffect(() => {
    fetchBenchmarks();
  }, [industry]);

  const fetchIndustries = async () => {
    try {
      const response = await axios.get(`${API}/industries`);
      setIndustries(response.data);
    } catch (error) {
      console.error('Error fetching industries:', error);
    }
  };

  const fetchBenchmarks = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/benchmarking?industry=${industry}`);
      setBenchmarks(response.data);
    } catch (error) {
      console.error('Error fetching benchmarks:', error);
    } finally {
      setLoading(false);
    }
  };

  const currentIndustry = industries.find(i => i.id === industry);

  const getStatusColor = (status) => {
    return status === 'within' ? 'text-[#2D4A3E]' : 'text-[#C85A32]';
  };

  const getStatusBg = (status) => {
    return status === 'within' ? 'bg-[#2D4A3E]/10' : 'bg-[#C85A32]/10';
  };

  const getStatusIcon = (status, deviation) => {
    if (status === 'within') {
      return <TrendingDown className="w-5 h-5 text-[#2D4A3E]" />;
    }
    return <TrendingUp className="w-5 h-5 text-[#C85A32]" />;
  };

  const aboveBenchmark = benchmarks.filter(b => b.status === 'above').length;
  const withinBenchmark = benchmarks.filter(b => b.status === 'within').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1A362D]"></div>
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="benchmarking-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold tracking-tight text-[#111814] mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          Industry Benchmarking
        </h1>
        <p className="text-base text-[#4B5563] leading-relaxed">
          Compare your ESG metrics against industry standards and best practices
        </p>
      </div>

      {/* Industry Indicator */}
      <Card className="border-[#1A362D] border-2 mb-6 bg-gradient-to-r from-[#1A362D]/5 to-[#2D4A3E]/5" data-testid="industry-indicator">
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#1A362D] rounded-lg flex items-center justify-center">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-[#6B7280]">Selected Industry</p>
                <p className="text-lg font-bold text-[#111814]">{currentIndustry?.name || 'Office / Commercial'}</p>
              </div>
            </div>
            <Badge className="bg-[#1A362D] text-white text-xs">
              Benchmarks Active
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card className="border-[#E5E1D8]" data-testid="total-metrics-card">
          <CardContent className="pt-6">
            <p className="text-xs font-semibold uppercase tracking-widest text-[#6B7280] mb-2">Metrics Tracked</p>
            <p className="text-4xl font-bold text-[#111814]">{benchmarks.length}</p>
          </CardContent>
        </Card>
        <Card className="border-[#2D4A3E] border-2" data-testid="within-benchmark-card">
          <CardContent className="pt-6">
            <p className="text-xs font-semibold uppercase tracking-widest text-[#2D4A3E] mb-2">Within Benchmark</p>
            <p className="text-4xl font-bold text-[#2D4A3E]">{withinBenchmark}</p>
            <p className="text-sm text-[#6B7280] mt-1">Good performance</p>
          </CardContent>
        </Card>
        <Card className="border-[#C85A32] border-2" data-testid="above-benchmark-card">
          <CardContent className="pt-6">
            <p className="text-xs font-semibold uppercase tracking-widest text-[#C85A32] mb-2">Above Benchmark</p>
            <p className="text-4xl font-bold text-[#C85A32]">{aboveBenchmark}</p>
            <p className="text-sm text-[#6B7280] mt-1">Needs improvement</p>
          </CardContent>
        </Card>
      </div>

      {/* Benchmark Source Info */}
      <Card className="border-[#E5E1D8] mb-6">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Info className="w-5 h-5 text-[#7898A5]" />
            <CardTitle className="text-lg" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Benchmark Sources
            </CardTitle>
          </div>
          <CardDescription>
            {currentIndustry?.source || 'Benchmarks derived from Bureau of Energy Efficiency (BEE), International Energy Agency (IEA), and CPCB industry norms.'}
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Benchmark Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {benchmarks.map((benchmark, index) => (
          <Card 
            key={index} 
            className={`border-2 ${benchmark.status === 'within' ? 'border-[#2D4A3E]/30' : 'border-[#C85A32]/30'} transition-all duration-200 hover:shadow-sm`}
            data-testid={`benchmark-card-${index}`}
          >
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="text-lg" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                    {benchmark.metric}
                  </CardTitle>
                  <CardDescription className="mt-1">Unit: {benchmark.unit}</CardDescription>
                </div>
                <div className={`p-2 rounded-lg ${getStatusBg(benchmark.status)}`}>
                  {getStatusIcon(benchmark.status, benchmark.deviation_percent)}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Values Comparison */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="p-3 bg-[#F9F8F6] rounded-lg">
                  <p className="text-xs font-semibold uppercase tracking-widest text-[#6B7280] mb-1">Your Value</p>
                  <p className="text-2xl font-bold text-[#111814]">{benchmark.actual}</p>
                </div>
                <div className="p-3 bg-[#F9F8F6] rounded-lg">
                  <p className="text-xs font-semibold uppercase tracking-widest text-[#6B7280] mb-1">Benchmark</p>
                  <p className="text-2xl font-bold text-[#6B7280]">{benchmark.benchmark}</p>
                </div>
              </div>

              {/* Deviation Indicator */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-[#6B7280]">Deviation from benchmark</span>
                  <span className={`text-sm font-semibold ${getStatusColor(benchmark.status)}`}>
                    {benchmark.deviation_percent > 0 ? '+' : ''}{benchmark.deviation_percent.toFixed(1)}%
                  </span>
                </div>
                <div className="relative h-3 bg-[#F2F0EB] rounded-full overflow-hidden">
                  <div className="absolute inset-y-0 left-1/2 w-0.5 bg-[#6B7280]"></div>
                  <div 
                    className={`absolute inset-y-0 ${benchmark.deviation_percent >= 0 ? 'left-1/2' : 'right-1/2'} ${benchmark.status === 'within' ? 'bg-[#2D4A3E]' : 'bg-[#C85A32]'} transition-all duration-500`}
                    style={{ 
                      width: `${Math.min(Math.abs(benchmark.deviation_percent), 50)}%`,
                    }}
                  ></div>
                </div>
              </div>

              {/* Status Badge */}
              <div className={`p-3 rounded-lg ${getStatusBg(benchmark.status)}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-xs font-semibold uppercase tracking-widest ${getStatusColor(benchmark.status)}`}>
                    {benchmark.status === 'within' ? 'Within Benchmark' : 'Above Benchmark'}
                  </span>
                </div>
                <p className="text-sm text-[#4B5563] leading-relaxed">{benchmark.explanation}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Legend */}
      <div className="mt-8 p-4 bg-[#F9F8F6] rounded-lg border border-[#E5E1D8]">
        <p className="text-xs font-semibold uppercase tracking-widest text-[#6B7280] mb-3">Legend</p>
        <div className="flex flex-wrap gap-6">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-[#2D4A3E]"></div>
            <span className="text-sm text-[#4B5563]">Within Benchmark (Good)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-[#C85A32]"></div>
            <span className="text-sm text-[#4B5563]">Above Benchmark (Needs Improvement)</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Benchmarking;
